import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';

import CompanyForm from './components/CompanyForm';
import CompanyGrid from './components/CompanyGrid';
import PlantManagement from './components/PlantManagement';

const CompanyManagement = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSector, setFilterSector] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase?.from('companies')?.select(`
          *
        `)?.order('created_at', { ascending: false });

      if (error) throw error;
      
      // Get plant counts separately to avoid column conflicts
      const companiesWithCounts = await Promise.all(
        (data || [])?.map(async (company) => {
          const { count } = await supabase
            ?.from('plants')
            ?.select('*', { count: 'exact', head: true })
            ?.eq('company_id', company?.id);
          
          return {
            ...company,
            plantCount: count || 0
          };
        })
      );
      
      setCompanies(companiesWithCounts);
    } catch (error) {
      console.error('Error loading companies:', error?.message);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (companyData) => {
    try {
      setError(null);
      
      const { data, error } = await supabase?.from('companies')?.insert([{
          name: companyData?.name,
          industry_sector: companyData?.sector,
          address: companyData?.address,
          city: companyData?.city,
          state: companyData?.state,
          country: companyData?.country,
          postal_code: companyData?.postalCode,
          phone: companyData?.phone,
          email: companyData?.email,
          website: companyData?.website,
          created_by: user?.id
        }])?.select()?.single();

      if (error) throw error;
      
      await loadCompanies();
      setIsFormOpen(false);
      setEditingCompany(null);
    } catch (error) {
      console.error('Error creating company:', error?.message);
      setError('Failed to create company');
    }
  };

  const handleUpdateCompany = async (companyData) => {
    try {
      setError(null);
      
      const { data, error } = await supabase?.from('companies')?.update({
          name: companyData?.name,
          industry_sector: companyData?.sector,
          address: companyData?.address,
          city: companyData?.city,
          state: companyData?.state,
          country: companyData?.country,
          postal_code: companyData?.postalCode,
          phone: companyData?.phone,
          email: companyData?.email,
          website: companyData?.website
        })?.eq('id', editingCompany?.id)?.select()?.single();

      if (error) throw error;
      
      await loadCompanies();
      setIsFormOpen(false);
      setEditingCompany(null);
      
      if (selectedCompany?.id === editingCompany?.id) {
        setSelectedCompany(data);
      }
    } catch (error) {
      console.error('Error updating company:', error?.message);
      setError('Failed to update company');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        setError(null);
        
        const { error } = await supabase?.from('companies')?.delete()?.eq('id', companyId);

        if (error) throw error;
        
        await loadCompanies();
        
        if (selectedCompany?.id === companyId) {
          setSelectedCompany(null);
        }
      } catch (error) {
        console.error('Error deleting company:', error?.message);
        setError('Failed to delete company');
      }
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setIsFormOpen(true);
  };

  const handleAddPlant = async (plant) => {
    try {
      setError(null);
      
      const { data, error } = await supabase?.from('plants')?.insert([{
          company_id: selectedCompany?.id,
          name: plant?.name,
          location: plant?.location,
          address: plant?.address,
          city: plant?.city,
          state: plant?.state,
          country: plant?.country,
          postal_code: plant?.postalCode,
          capacity: plant?.capacity,
          operational_since: plant?.operationalSince
        }])?.select()?.single();

      if (error) throw error;
      
      await loadCompanies();
      await loadSelectedCompanyPlants();
    } catch (error) {
      console.error('Error adding plant:', error?.message);
      setError('Failed to add plant');
    }
  };

  const handleUpdatePlant = async (plantId, plantData) => {
    try {
      setError(null);
      
      const { data, error } = await supabase?.from('plants')?.update({
          name: plantData?.name,
          location: plantData?.location,
          address: plantData?.address,
          city: plantData?.city,
          state: plantData?.state,
          country: plantData?.country,
          postal_code: plantData?.postalCode,
          capacity: plantData?.capacity,
          operational_since: plantData?.operationalSince
        })?.eq('id', plantId)?.select()?.single();

      if (error) throw error;
      
      await loadSelectedCompanyPlants();
    } catch (error) {
      console.error('Error updating plant:', error?.message);
      setError('Failed to update plant');
    }
  };

  const handleDeletePlant = async (plantId) => {
    if (window.confirm('Are you sure you want to delete this plant?')) {
      try {
        setError(null);
        
        const { error } = await supabase?.from('plants')?.delete()?.eq('id', plantId);

        if (error) throw error;
        
        await loadCompanies();
        await loadSelectedCompanyPlants();
      } catch (error) {
        console.error('Error deleting plant:', error?.message);
        setError('Failed to delete plant');
      }
    }
  };

  const loadSelectedCompanyPlants = async () => {
    if (!selectedCompany?.id) return;
    
    try {
      const { data, error } = await supabase?.from('plants')?.select('*')?.eq('company_id', selectedCompany?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      
      setSelectedCompany(prev => ({
        ...prev,
        plants: data || []
      }));
    } catch (error) {
      console.error('Error loading plants:', error?.message);
    }
  };

  useEffect(() => {
    if (selectedCompany?.id) {
      loadSelectedCompanyPlants();
    }
  }, [selectedCompany?.id]);

  const filteredCompanies = companies?.filter(company => {
    const matchesSearch = company?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesSector = filterSector === 'all' || company?.industry_sector === filterSector;
    return matchesSearch && matchesSector;
  });

  const sectors = [...new Set(companies.map(c => c?.industry_sector).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading companies...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-6 py-8">
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: 'Company Management', path: '/company-management' }
          ]}
        />

        <div className="flex items-center justify-between mb-8 mt-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Company Management</h1>
            <p className="text-muted-foreground mt-2">Manage companies, plants, and organizational settings</p>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            onClick={() => {
              setEditingCompany(null);
              setIsFormOpen(true);
            }}
          >
            Add Company
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search companies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full h-10 px-4 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <select
                  value={filterSector}
                  onChange={(e) => setFilterSector(e?.target?.value)}
                  className="h-10 px-4 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">All Sectors</option>
                  {sectors?.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
              </div>

              <CompanyGrid
                companies={filteredCompanies}
                onEdit={handleEditCompany}
                onDelete={handleDeleteCompany}
                onSelect={setSelectedCompany}
                selectedCompanyId={selectedCompany?.id}
              />
            </div>
          </div>

          <div className="lg:col-span-1">
            <PlantManagement
              company={selectedCompany}
              onAddPlant={handleAddPlant}
              onUpdatePlant={handleUpdatePlant}
              onDeletePlant={handleDeletePlant}
            />
          </div>
        </div>
      </div>
      {isFormOpen && (
        <CompanyForm
          company={editingCompany}
          onSubmit={editingCompany ? handleUpdateCompany : handleCreateCompany}
          onClose={() => {
            setIsFormOpen(false);
            setEditingCompany(null);
          }}
        />
      )}
    </div>
  );
};

export default CompanyManagement;